import { Droppable } from "@hello-pangea/dnd";
import { ActivityKanbanCard } from "./ActivityKanbanCard";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { ActivityDetail, FazerAmizadeEtapa } from "@/types/activity";

interface EtapaConfig {
  id: FazerAmizadeEtapa;
  nome: string;
  cor: string;
  tipo: 'ativa' | 'sucesso' | 'falha';
}

interface ActivityKanbanColumnProps {
  etapa: EtapaConfig;
  activities: ActivityDetail[];
  onCardClick: (activity: ActivityDetail) => void;
}

const COR_CLASSES: Record<string, { bg: string; border: string; text: string; footer: string }> = {
  slate: { bg: 'bg-slate-100', border: 'border-slate-300', text: 'text-slate-700', footer: 'bg-slate-200' },
  blue: { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-700', footer: 'bg-blue-200' },
  green: { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-700', footer: 'bg-green-200' },
  red: { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-700', footer: 'bg-red-200' },
};

export function ActivityKanbanColumn({ etapa, activities, onCardClick }: ActivityKanbanColumnProps) {
  const cores = COR_CLASSES[etapa.cor] || COR_CLASSES.slate;

  return (
    <div className="shrink-0 flex flex-col w-72">
      {/* Header da coluna */}
      <div className={cn("rounded-t-lg px-3 py-2 border", cores.bg, cores.border)}>
        <div className="flex items-center justify-between">
          <h3 className={cn("font-semibold text-sm", cores.text)}>
            {etapa.nome}
          </h3>
          <Badge
            variant="secondary"
            className={cn("text-xs font-medium px-2 py-0.5", cores.bg, cores.text)}
          >
            {activities.length}
          </Badge>
        </div>
      </div>

      {/* Area droppable */}
      <Droppable droppableId={etapa.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={cn(
              "min-h-[400px] flex-1 p-2 space-y-2 border border-t-0",
              snapshot.isDraggingOver ? "bg-slate-100" : "bg-slate-50/50"
            )}
          >
            {activities.map((activity, index) => (
              <ActivityKanbanCard
                key={activity.id}
                activity={activity}
                index={index}
                onClick={() => onCardClick(activity)}
              />
            ))}
            {provided.placeholder}

            {activities.length === 0 && (
              <div className="flex items-center justify-center h-32 text-slate-400 text-sm">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-slate-100 flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-dashed border-slate-300 rounded" />
                  </div>
                  Nenhuma atividade
                </div>
              </div>
            )}
          </div>
        )}
      </Droppable>

      {/* Footer da coluna */}
      <div className="bg-white/80 backdrop-blur-xs border border-slate-200 border-t-0 rounded-b-lg p-2">
        <div className={cn("h-1 rounded-full", cores.footer)} />
      </div>
    </div>
  );
}
